/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import * as d3 from 'd3';
import { saveAs } from 'file-saver';
import html2canvas, { Options } from 'html2canvas';
import { SetterOrUpdater } from 'recoil';
import { ReferenceArea } from 'recharts';
import {
  DEFAULT_BIAXIAL_ZOOM_CONFIG,
  DEFAULT_CHART_LOOKBACK_TRADING_DAYS,
  DEFAULT_ZOOM_CONFIG,
} from '../../config';
import {
  BiaxialChartZoomConfig,
  ChartZoomConfig,
  Bounds,
  BrushZoomConfig,
} from '../../types';
import sfProDisplayRegularUrl from '../../webFonts/SF_Pro_Display/SFPRODISPLAYREGULAR.woff2?url';
import sfProDisplayMediumUrl from '../../webFonts/SF_Pro_Display/SFPRODISPLAYMEDIUM.woff2?url';
import sfProDisplayBoldUrl from '../../webFonts/SF_Pro_Display/SFPRODISPLAYBOLD.woff2?url';

// Zoom
export const getAxisYDomain = (
  from: number,
  to: number,
  yFields: string[],
  xField: string,
  offset: number,
  initialData?: any[],
) => {
  const refData = (initialData || []).filter(
    (d) => d[xField] >= from && d[xField] <= to,
  );
  if (refData.length > 0) {
    const allValues: number[] = refData.flatMap((item) =>
      yFields.map((key) => item[key]).filter((value) => value != null),
    );
    const minValue = allValues.reduce(
      (min, value) => (value < min ? value : min),
      Number.POSITIVE_INFINITY,
    );
    const maxValue = allValues.reduce(
      (max, value) => (value > max ? value : max),
      Number.NEGATIVE_INFINITY,
    );
    return [minValue - offset, maxValue + offset];
  } else {
    return [from, to];
  }
};

export const isZoomConfigEqDefault = (
  zoomConfig: BiaxialChartZoomConfig | ChartZoomConfig,
  overrideDefault?: any,
): boolean => {
  const originalDefault =
    'top2' in zoomConfig ? DEFAULT_BIAXIAL_ZOOM_CONFIG : DEFAULT_ZOOM_CONFIG;
  return Object.entries(overrideDefault ?? originalDefault)
    .filter(
      ([key, _]) => !['data', 'refAreaLeft', 'refAreaRight'].includes(key),
    )
    .some(([key, value]) =>
      'top2' in zoomConfig
        ? zoomConfig[key as keyof BiaxialChartZoomConfig] !== value
        : zoomConfig[key as keyof ChartZoomConfig] !== value,
    );
};

function getBounds(strikes: number[], zoomConfig: Bounds) {
  const [min, max] = d3.extent(strikes) as [number, number];
  const left = parseFloat(`${zoomConfig.left}`);
  const right = parseFloat(`${zoomConfig.right}`);
  return {
    left: isNaN(right) ? min : left,
    right: isNaN(right) ? max : right,
  };
}

function getTickRange(left: number, right: number, step: number): number[] {
  const [min, max] = left < right ? [left, right] : [right, left];
  const start = Math.ceil(min / step) * step;
  const end = Math.ceil(max / step) * step;
  return d3.range(start, end, step);
}

export const STRIKE_TICK_CONFIG: Map<number, number> = new Map([
  [800, 100],
  [400, 50],
  [200, 25],
  [50, 10],
  [25, 5],
  [15, 2],
  [5, 1],
  [2, 0.25],
  [1, 0.1],
  [0.1, 0.02],
]);

export const DAY_IN_MS = 86_400_000; // Number of milliseconds in a day
export const WEEK_IN_MS = 604_800_000; // Number of milliseconds in a week
export const MONTH_IN_MS = 2_629_800_000; // Average number of milliseconds in a month (30.44 days)
export const YEAR_IN_MS = 365 * DAY_IN_MS;

export const TIMESTAMP_TICK_CONFIG = new Map([
  [YEAR_IN_MS * 2, MONTH_IN_MS * 4], // >= 2 years, step is 4 months
  [MONTH_IN_MS * 6, MONTH_IN_MS], // >= 6 months, step is 1 month
  [MONTH_IN_MS * 3, WEEK_IN_MS * 2], // >= 3 months, step is 2 weeks
  [MONTH_IN_MS, DAY_IN_MS * 5], // >= 1 month, step is 5 days
  [WEEK_IN_MS * 2, DAY_IN_MS * 2], // >= 2 weeks, step is 2 days
  [WEEK_IN_MS, DAY_IN_MS], // >= 1 week, step is 1 day
  [DAY_IN_MS, DAY_IN_MS], // >= 1 day, step is 1 day
]);

export const DTE_TICK_CONFIG = new Map([
  [730, 90], // >= 2 years, step is 1 quarter (approx.)
  [365, 30], // >= 1 year, step is 1 month (approx.)
  [180, 14], // >= half a year, step is 2 weeks (approx.)
  [90, 7], // >= 3 months, step is 1 week
  [30, 2], // >= 1 month, step is 2 days
  [1, 1], // >= 1 day, step is 1 day
]);

export const MONEYNESS_TICK_CONFIG = new Map([
  [0.5, 0.1], // >= 50% (0.5), step is 10% (0.1)
  [0.2, 0.05], // >= 20% (0.2), step is 5% (0.05)
  [0.1, 0.02], // >= 10% (0.1), step is 2% (0.02)
  [0.05, 0.01], // >= 5% (0.05), step is 1% (0.01)
  [0.01, 0.005], // >= 2% (0.02), step is 0.5% (0.005)
]);

export const DELTA_TICK_CONFIG = new Map([
  [0.5, 0.1], // >= 50% (0.5), step is 10% (0.1)
  [0.2, 0.05], // >= 20% (0.2), step is 5% (0.05)
  [0.05, 0.02], // >= 5% (0.05), step is 2% (0.02)
  [0.01, 0.01], // >= 1% (0.01), step is 1% (0.01)
]);

export const getTicks = (
  values: number[],
  zoomConfig: Bounds,
  tickConfig: Map<number, number>,
) => {
  const { left, right } = getBounds(values, zoomConfig);
  for (const [threshold, step] of tickConfig.entries()) {
    if (right - left >= threshold) {
      return getTickRange(left, right, step);
    }
  }
  return undefined; // let recharts use default ticks algorithm
};

export const getTicksBrushed = (
  values: number[],
  zoomConfig: BrushZoomConfig,
  tickConfig: Map<number, number>,
) => {
  const { leftIdx: left, rightIdx: right } = zoomConfig;
  if (left != null && right != null && values != null) {
    for (const [threshold, step] of tickConfig.entries()) {
      const leftVal = values[left];
      const rightVal = values[right];
      if (Math.abs(rightVal - leftVal) >= threshold) {
        return getTickRange(leftVal, rightVal, step);
      }
    }
  }
  return undefined; // let recharts use default ticks algorithm
};

/**
 * Returns the brush `leftIdx` that opens a chart on the most recent lookback rows.
 * Defaults to DEFAULT_CHART_LOOKBACK_TRADING_DAYS (63), approximately 3 months.
 */
export const getBrushLookbackLeftIdx = (
  dataLength: number,
  lookback: number = DEFAULT_CHART_LOOKBACK_TRADING_DAYS,
): number => Math.max(0, dataLength - lookback - 1);

export const updateBrushZoomConfig = (
  oldConfig: BrushZoomConfig,
  newData: any[],
  setBrushZoomConfig: SetterOrUpdater<BrushZoomConfig>,
  initialRightIdx?: number,
  initialLeftIdx?: number,
  reset?: boolean,
) => {
  if (
    reset ||
    oldConfig.data == null ||
    oldConfig.data.length === 0 ||
    (oldConfig.leftIdx === 0 &&
      oldConfig.rightIdx === oldConfig.data.length - 1)
  ) {
    setBrushZoomConfig((prev) => ({
      ...prev,
      leftIdx: initialLeftIdx ?? 0,
      rightIdx: initialRightIdx ?? newData.length - 1,
      data: newData,
    }));
    return;
  }

  const oldLength = oldConfig.data.length;
  const newLength = newData.length;

  let newleftIdx = Math.round(
    ((oldConfig.leftIdx ?? 0) / oldLength) * newLength,
  );
  let newrightIdx = Math.round(
    ((oldConfig.rightIdx ?? oldLength - 1) / oldLength) * newLength,
  );

  // Ensure the new indices are within bounds
  newleftIdx = Math.max(0, Math.min(newleftIdx, newLength - 1));
  newrightIdx = Math.max(0, Math.min(newrightIdx, newLength - 1));

  setBrushZoomConfig((prev) => ({
    ...prev,
    leftIdx: newleftIdx,
    rightIdx: newrightIdx,
    data: newData,
  }));
};

const CHART_LOGO_SIZE = 32;
const CHART_LOGO_PADDING = 12;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function toBase64DataUri(url: string, mimeType: string): Promise<string> {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  let binary = '';
  new Uint8Array(buffer).forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return `data:${mimeType};base64,${btoa(binary)}`;
}

let embeddedSvgFontFacesPromise: Promise<string> | null = null;

/**
 * html2canvas rasterizes <svg> by serializing it to a standalone image, which
 * runs in a sandbox that can't fetch our self-hosted @font-face fonts. Embed
 * the font data directly as base64 so it's self-contained.
 */
function getEmbeddedSvgFontFaces(): Promise<string> {
  embeddedSvgFontFacesPromise ??= Promise.all([
    toBase64DataUri(sfProDisplayRegularUrl, 'font/woff2'),
    toBase64DataUri(sfProDisplayMediumUrl, 'font/woff2'),
    toBase64DataUri(sfProDisplayBoldUrl, 'font/woff2'),
  ]).then(
    ([regular, medium, bold]) => `
      @font-face { font-family: 'SF Pro Display'; font-weight: 500; src: url(${regular}) format('woff2'); }
      @font-face { font-family: 'SF Pro Display'; font-weight: 500; src: url(${medium}) format('woff2'); }
      @font-face { font-family: 'SF Pro Display'; font-weight: 700; src: url(${bold}) format('woff2'); }
    `,
  );
  return embeddedSvgFontFacesPromise;
}

export const downloadChartAsPng = async (
  elementRef: React.RefObject<HTMLDivElement>,
  fileName: string,
  options?: Pick<Options, 'backgroundColor'>,
  logoSrc?: string,
): Promise<void> => {
  if (elementRef.current) {
    try {
      // Needed so the embedded @font-face below has a name to match against.
      const textNodes = Array.from(
        elementRef.current.querySelectorAll<SVGTextElement>('text'),
      );
      const previousFontFamilies = textNodes.map((node) =>
        node.getAttribute('font-family'),
      );
      textNodes.forEach((node) => {
        node.setAttribute(
          'font-family',
          window.getComputedStyle(node).fontFamily,
        );
      });

      const svgEl = elementRef.current.querySelector('svg');
      let fontStyleEl: SVGStyleElement | null = null;
      if (svgEl) {
        try {
          fontStyleEl = document.createElementNS(
            'http://www.w3.org/2000/svg',
            'style',
          );
          fontStyleEl.textContent = await getEmbeddedSvgFontFaces();
          svgEl.insertBefore(fontStyleEl, svgEl.firstChild);
        } catch (fontError) {
          console.error(`Error embedding fonts for ${fileName} PNG`, fontError);
          fontStyleEl = null;
        }
      }

      let canvas;
      try {
        canvas = await html2canvas(elementRef.current, options);
      } finally {
        textNodes.forEach((node, i) => {
          const previous = previousFontFamilies[i];
          if (previous == null) {
            node.removeAttribute('font-family');
          } else {
            node.setAttribute('font-family', previous);
          }
        });
        fontStyleEl?.remove();
      }

      if (logoSrc != null) {
        const ctx = canvas.getContext('2d');
        if (ctx) {
          try {
            // html2canvas leaves its own scale transform active on this
            // context; reset it or our coordinates get doubled too.
            ctx.setTransform(1, 0, 0, 1, 0, 0);

            // Canvas is scaled vs. the source element's CSS size; match it.
            const scale = canvas.width / elementRef.current.offsetWidth;
            const size = CHART_LOGO_SIZE * scale;
            const padding = CHART_LOGO_PADDING * scale;

            const logo = await loadImage(logoSrc);
            ctx.drawImage(
              logo,
              padding,
              canvas.height - size - padding,
              size,
              size,
            );
          } catch (logoError) {
            console.error(`Error drawing logo onto ${fileName} PNG`, logoError);
          }
        }
      }

      canvas.toBlob((blob: Blob | null) => {
        if (blob) {
          saveAs(blob, `${fileName}.png`);
        }
      });
    } catch (error) {
      console.error(`Error generating ${fileName} PNG`, error);
    }
  }
};

export const getZoomConfigRefArea = (
  zoomConfig: BrushZoomConfig | ChartZoomConfig | BiaxialChartZoomConfig,
  yAxisId?: string,
) =>
  zoomConfig.refAreaLeft !== '' && zoomConfig.refAreaRight !== '' ? (
    <ReferenceArea
      {...(yAxisId ? { yAxisId } : {})}
      x1={zoomConfig.refAreaLeft}
      x2={zoomConfig.refAreaRight}
      strokeOpacity={0.3}
      isFront
    />
  ) : null;
